import type { FieldDef } from '~/components/form/utils';
import { ELEVATOR_TYPE_LABELS } from '~/components/portfolio/propertyCarColumns';
import type { ElevatorTypeEnum } from '~/data/generated/model';
import { yearOptions } from '~/data/schemas/car';
import type { OEM } from '~/data/schemas/oem';
import { LocaleTerms } from '~/hooks/useLocaleTerms';

interface CarFieldDefParams {
  oems?: OEM[] | undefined;
  terms: LocaleTerms;
}

export const buildCarFieldDef = ({ oems, terms }: CarFieldDefParams): FieldDef[] => {
  return [
    {
      name: 'ahjId',
      label: 'License',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'name',
      label: `${terms.unit} Name`,
      type: 'text',
      required: true,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'criticalUnit',
      label: `Critical ${terms.unit}`,
      type: 'checkbox',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'elevatorType',
      label: 'Device Type',
      type: 'autocomplete',
      options: (Object.entries(ELEVATOR_TYPE_LABELS) as [ElevatorTypeEnum, string][]).map(
        ([type, label]) => ({ value: type, label, id: type }),
      ),
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'installationYear',
      label: 'Installation Year',
      type: 'autocomplete',
      options: yearOptions.map((year) => ({
        label: year,
        id: year,
      })),
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'modernizationYear',
      label: 'Modernization Year',
      type: 'autocomplete',
      options: yearOptions.map((year) => ({
        label: year,
        id: year,
      })),
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'numLandings',
      label: 'Number of Landings',
      type: 'number',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'speedFpm',
      label: 'Speed (FPM)',
      type: 'number',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'capacityLbs',
      label: 'Capacity (lbs)',
      type: 'number',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'controllerModel',
      label: 'Controller Model',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'driveType',
      label: 'Drive Type',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'lastSspiTest',
      label: 'Last SSPI Test',
      type: 'date',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'numEntrances',
      label: 'Number of Entrances',
      type: 'number',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'oem',
      label: 'OEM',
      type: 'autocomplete',
      options:
        oems?.map((oem) => ({
          label: oem.name,
          id: oem.id,
        })) ?? [],
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'lastCat1Test',
      label: 'Last CAT-1 Test',
      type: 'date',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'lastCat5Test',
      label: 'Last CAT-5 Test',
      type: 'date',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
  ];
};
